import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Modal, TreeSelect } from '@maxtropy/components';
import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import {
  V2HighEnergyDeviceDeviceTreePostResponse,
  apiV2HighEnergyDeviceDeviceTreePost,
  apiV2HighEnergyDeviceRecalculatePost,
} from '@maxtropy/device-customer-apis-v2';
import useLatestRequest from '../useLatestRequest';
export type DeviceTreeData = Exclude<V2HighEnergyDeviceDeviceTreePostResponse['list'], undefined>[number];
const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface CalcModalProps {
  isShow: (value: boolean) => void;
}

interface MyTreeNode {
  key: string;
  title: string;
  value: string;
  children?: MyTreeNode[];
  [key: string]: any;
}

const CalcModal: React.FC<CalcModalProps> = ({ isShow }) => {
  const [form] = Form.useForm();
  const [value, setValue] = useState<RangeValue>(null);
  const [dates, setDates] = useState<RangeValue>();
  const [deviceOp, setDeviceOp] = useState<MyTreeNode[]>([]);
  const { runLatest: runLatestTree } = useLatestRequest();
  const { runLatest: runLatestRecalculate } = useLatestRequest();

  useEffect(() => {
    runLatestTree(() => apiV2HighEnergyDeviceDeviceTreePost({}), {
      onSuccess: res => {
        const treeData = transformTree(res.list);
        setDeviceOp(treeData);
      },
    });
  }, [runLatestTree]);

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 6;
    const tooEarly = dates[1] && dates[1].diff(current, 'months') >= 6;
    const afterToday = current && current > dayjs().endOf('day');
    return !!tooEarly || !!tooLate || !!afterToday;
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(values => {
        const { date, unit } = values;
        runLatestRecalculate(
          () =>
            apiV2HighEnergyDeviceRecalculatePost({
              inventoryIds: unit,
              fromTime: dayjs(date[0]).startOf('day').valueOf(),
              toTime: dayjs(date[1]).endOf('day').valueOf(),
            }),
          {
            onSuccess: () => isShow(false),
          }
        );
      })
      .catch(() => undefined);
  };

  return (
    <>
      <Modal
        title="重计算"
        forceRender
        className={styles.calcModalBox}
        open
        afterClose={() => form.resetFields()}
        onCancel={() => isShow(false)}
        contentClassName="modal-form-content"
        footer={[
          <Button key="back" onClick={() => isShow(false)} className={styles.cancelBtn}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={onFinish}>
            确定
          </Button>,
        ]}
      >
        <div className={styles.modalBody}>
          <div className={styles.tipWrapper}>
            <ExclamationCircleOutlined style={{ color: 'var(--mx-warning-color)', fontSize: 12 }} />
            <span className={styles.txt}>重计算数值会覆盖现有数据</span>
          </div>

          <Form form={form}>
            <Form.Item label="选择时间" name="date" rules={[{ required: true, message: '请选择时间' }]}>
              <RangePicker
                value={value || dates}
                disabledDate={disabledDate}
                onCalendarChange={val => {
                  setDates(val);
                }}
                onChange={val => {
                  setValue(val);
                }}
                onOpenChange={onOpenChange}
              />
            </Form.Item>
            <Form.Item label="高能耗设备" name="unit" rules={[{ required: true, message: '请选择用能单元' }]}>
              <TreeSelect
                allowClear
                maxTagCount={3}
                placeholder="请选择"
                treeDefaultExpandAll
                showSearch
                treeNodeFilterProp="title"
                treeCheckable={true}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                treeData={deviceOp}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default CalcModal;
// 使得返回的数据变成DataNode格式
export const transformTree = (tree?: DeviceTreeData[]): MyTreeNode[] => {
  if (!tree) return [];
  return tree.map(item => {
    return {
      key: item.deviceTypeId?.toString(),
      title: item.deviceTypeName,
      value: item.deviceTypeId?.toString(),
      info: {
        name: item.deviceTypeName,
        id: item.deviceTypeId,
      },
      children: (item.deviceLedgerInfo ?? []).map(c => {
        return {
          key: c.id?.toString(),
          title: c.assetDeviceName,
          value: c.id?.toString(),
          info: {
            name: c.assetDeviceName,
            id: c.energyUnitId,
            deviceLedgerId: c.id,
          },
        };
      }),
    };
  }) as MyTreeNode[];
};
